import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpRequest, HttpInterceptor, HttpHandler, HttpErrorResponse } from '@angular/common/http';

import { Observable, ObservableInput, fromEvent, throwError, retry, take, tap, timeout, catchError } from 'rxjs';

import { WindowRef } from '../providers';
import { ConfigService, SentryLogger } from '../services';
import { mapErrorForSentry } from '../utils';

const AKAMAI_CHALLENGE_COMPLETE_EVENT_TIMEOUT = 30000; // 30 seconds

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private windowRef = inject(WindowRef);
  private configService = inject(ConfigService);
  private sentryLogger = inject(SentryLogger);

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const akamaiCryptoChallengeUrls = [
      `${this.configService.cfg.casHost}/cas/login`,
      this.configService.cfg.loyaltyServices.legacy.loyaltyMemberUrl,
    ];

    if (request.method === 'POST' && akamaiCryptoChallengeUrls.some((url) => request.url.includes(url))) {
      return next.handle(request).pipe(
        retry({
          count: 1,
          delay: (error: HttpErrorResponse): ObservableInput<Event> => {
            if (error.status !== 428) {
              return throwError(() => error);
            }

            return fromEvent(this.windowRef.nativeWindow.document, 'akamai-challenge-noreload').pipe(
              take(1),
              tap((akamaiEvent) => {
                // TEMPORARY LOGGING FOR DEBUGGING PURPOSES
                console.log('Akamai challenge completed, retrying request...', { akamaiEvent });
              }),
              timeout(AKAMAI_CHALLENGE_COMPLETE_EVENT_TIMEOUT), // 30 second timeout
              catchError((err: unknown) => {
                this.sentryLogger.error('Error while waiting for the Akamai challenge completion event', {
                  error: mapErrorForSentry(err),
                  httpErrorResponse: mapErrorForSentry(error),
                });
                return throwError(() => error); // Re-throw the original 428 error
              })
            );
          },
        })
      );
    }
    return next.handle(request);
  }
}
